/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionExpired = params.get("session_expired") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Iter-56b — owner accounts land on the Cost Dashboard by default
      // (Command Center is now their primary workflow). Regular customers
      // continue to the customer dashboard unchanged. The Cost Dashboard
      // still exposes a visible "Customer dashboard" link in its top-right
      // nav so the owner can hop over for QA whenever needed.
      const data = await login(email, password);
      toast.success("Welcome back");
      navigate(data?.is_owner ? "/admin/cost" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-900 mb-10 transition-colors" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="max-w-sm w-full">
          <div className="w-10 h-10 rounded-xl bg-charcoal-900 grid place-items-center mb-6">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-charcoal-900">Sign in</h1>
          <p className="text-charcoal-500 mt-2 text-sm">Welcome back to Mission Watch.</p>

          {sessionExpired && (
            <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                 data-testid="login-session-expired">
              Your session expired. Please sign in again to continue.
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-medium text-charcoal-700">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                placeholder="you@nonprofit.org" data-testid="login-email" />
            </div>
            <div>
              <label className="text-xs font-medium text-charcoal-700">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-xl border border-black/10 bg-white text-charcoal-900 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15 transition"
                placeholder="••••••••" data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-11 rounded-xl bg-brand text-white font-medium text-sm hover:bg-brand-hover transition-colors disabled:opacity-60"
              data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="flex items-center justify-between mt-4">
            <Link to="/forgot-password" className="text-sm text-charcoal-500 hover:text-charcoal-900 hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <p className="text-sm text-charcoal-500 mt-6">
            No account? <Link to="/register" className="text-brand font-medium hover:underline" data-testid="go-register">Start your first checkup</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-charcoal-900 tech-grid overflow-hidden">
        <div className="absolute inset-0 flex flex-col justify-center px-16">
          <p className="text-emerald-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">Peace of mind, monthly</p>
          <p className="text-white text-3xl font-medium leading-snug tracking-tight max-w-md">
            "Finally… someone is watching the little things so I don't have to."
          </p>
        </div>
      </div>
    </div>
  );
}
